import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';

interface ModelVersion {
  id: number;
  version: string;
  architecture: string;
  training_samples: number;
  val_iou: number;
  train_iou: number | null;
  deployed: boolean;
  deployment_date: string | null;
  model_path: string;
  notes: string | null;
  created_at: string;
}

const AdminModels: React.FC = () => {
  const navigate = useNavigate();
  const [models, setModels] = useState<ModelVersion[]>([]);
  const [currentModel, setCurrentModel] = useState<ModelVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deployingId, setDeployingId] = useState<number | null>(null);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const [modelsRes, currentRes] = await Promise.all([
        axios.get('/api/models/versions'),
        axios.get('/api/models/current')
      ]);

      setModels(modelsRes.data);
      if (currentRes.data.version) {
        setCurrentModel(currentRes.data);
      }
      setLoading(false);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError('Failed to load model versions');
      }
      setLoading(false);
    }
  };

  const handleDeployModel = async (modelId: number) => {
    if (!window.confirm('Are you sure you want to deploy this model? This will undeploy the current model and restart the inference API.')) {
      return;
    }

    setDeployingId(modelId);
    try {
      await axios.post(`/api/models/versions/${modelId}/deploy`);
      alert('Model deployed successfully! Remember to restart the inference API service on the server.');
      fetchModels();
    } catch (err) {
      alert('Failed to deploy model');
    } finally {
      setDeployingId(null);
    }
  };

  const getIoUBadge = (iou: number) => {
    if (iou >= 0.90) return { class: 'badge-success', label: 'Excellent' };
    if (iou >= 0.70) return { class: 'badge-primary', label: 'Good' };
    if (iou >= 0.50) return { class: 'badge-warning', label: 'Fair' };
    return { class: 'badge-error', label: 'Poor' };
  };

  if (loading) {
    return (
      <div className="loading-page">
        <p>Loading model versions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="container" style={{ paddingTop: '2rem' }}>
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="page-header-left">
              <button onClick={() => navigate('/admin')} className="btn btn-sm btn-ghost">
                ← Back
              </button>
              <h1 className="page-title">🤖 Model Management</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* Current Model */}
          {currentModel && (
            <div className="alert alert-success" style={{ marginBottom: 'var(--spacing-6)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                  Currently Deployed Model
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap', fontSize: 'var(--text-sm)' }}>
                  <span><strong>Version:</strong> {currentModel.version}</span>
                  <span><strong>Architecture:</strong> {currentModel.architecture}</span>
                  <span><strong>Val IoU:</strong> {(currentModel.val_iou * 100).toFixed(2)}%</span>
                  <span><strong>Deployed:</strong> {currentModel.deployment_date ? new Date(currentModel.deployment_date).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Model Comparison Chart */}
          <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
            <div className="card-header">
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', margin: 0 }}>
                Performance Comparison
              </h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                {models.slice(0, 5).map((model) => {
                  const iouBadge = getIoUBadge(model.val_iou);
                  return (
                    <div key={model.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
                      <div style={{ width: '120px', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                        {model.version}
                      </div>
                      <div style={{ flex: 1, height: '32px', background: 'var(--color-gray-100)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', position: 'relative' }}>
                        <div style={{ 
                          width: `${model.val_iou * 100}%`, 
                          height: '100%', 
                          background: model.deployed ? 'var(--color-success)' : 'var(--color-primary)',
                          transition: 'width 0.3s ease'
                        }} />
                        <div style={{ position: 'absolute', top: '50%', left: '12px', transform: 'translateY(-50%)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-white)' }}>
                          {(model.val_iou * 100).toFixed(2)}%
                        </div>
                      </div>
                      <span className={`badge ${iouBadge.class}`}>
                        {iouBadge.label}
                      </span>
                      {model.deployed && (
                        <span className="badge badge-success">✓ Deployed</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Models Table */}
          <div className="card">
            <div className="card-header">
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', margin: 0 }}>
                All Model Versions
              </h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Architecture</th>
                    <th>Training Samples</th>
                    <th>Val IoU</th>
                    <th>Train IoU</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="empty-state">
                          <div className="empty-state-title">No model versions found</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    models.map((model) => {
                      const iouBadge = getIoUBadge(model.val_iou);
                      return (
                        <tr key={model.id}>
                          <td>
                            <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)' }}>
                              {model.version}
                            </div>
                          </td>
                          <td style={{ fontSize: 'var(--text-sm)' }}>{model.architecture}</td>
                          <td>{model.training_samples.toLocaleString()}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                              <strong>{(model.val_iou * 100).toFixed(2)}%</strong>
                              <span className={`badge ${iouBadge.class}`} style={{ fontSize: 'var(--text-xs)' }}>
                                {iouBadge.label}
                              </span>
                            </div>
                          </td>
                          <td>
                            {model.train_iou ? `${(model.train_iou * 100).toFixed(2)}%` : 'N/A'}
                          </td>
                          <td>
                            {model.deployed ? (
                              <span className="badge badge-success">✓ Deployed</span>
                            ) : (
                              <span className="badge badge-secondary">Inactive</span>
                            )}
                          </td>
                          <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                            {new Date(model.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            {!model.deployed && (
                              <button
                                onClick={() => handleDeployModel(model.id)}
                                className="btn btn-sm btn-primary"
                                disabled={deployingId === model.id}
                              >
                                {deployingId === model.id ? 'Deploying...' : 'Deploy'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes Section */}
          <div className="alert alert-warning" style={{ marginTop: 'var(--spacing-6)' }}>
            <div>
              <div style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                ⚠️ Important Notes
              </div>
              <ul style={{ margin: 0, paddingLeft: 'var(--spacing-5)', fontSize: 'var(--text-sm)' }}>
                <li>After deploying a model, you must restart the inference API service on the server</li>
                <li>Model deployment will temporarily interrupt ongoing analyses</li>
                <li>Always test new models in a staging environment before production deployment</li>
                <li>Keep at least 2-3 recent model versions for rollback capability</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminModels;
